/**
 * Qualification des correspondances : ce que l'agent doit trancher, une par une.
 *
 * Le service de vérification rend des entités dont il ne fournit pas toujours la date de
 * naissance. Sans ce discriminant, rien ne permet de conclure automatiquement — d'où un jugement
 * humain, dossier par dossier.
 */

/** État d'un élément de comparaison entre le client et l'entité signalée. */
export enum DiscriminantStatus {
  /** Les deux valeurs concordent entièrement : argument pour retenir. */
  CONCORDANTE = "CONCORDANTE",
  /**
   * Compatibles, mais l'une est moins précise que l'autre (année seule face à une date complète).
   * Compatible n'est pas concordant : une année de naissance est partagée par des millions de
   * personnes, et l'annoncer « concordant » invitait à retenir une correspondance sur presque rien.
   */
  PARTIELLE = "PARTIELLE",
  /** Les deux valeurs existent et diffèrent : argument pour écarter. */
  DIFFERENTE = "DIFFERENTE",
  /**
   * On ne peut rien conclure : l'information manque, ou elle est là mais inexploitable. Elle ne
   * prouve rien, ni dans un sens ni dans l'autre : c'est précisément ce qui appelle un jugement
   * humain. Le cas le plus fréquent.
   */
  INDISPONIBLE = "INDISPONIBLE",
}

/** Jugement porté sur une correspondance. */
export enum MatchQualification {
  /** C'est bien cette personne. */
  CONFIRMED = "CONFIRMED",
  /** Ce n'est pas elle. */
  DISCARDED = "DISCARDED",
}

/**
 * Motif normalisé d'écartement. Le texte libre explique le cas ; ce code permet de compter —
 * sans lui, on ne saurait jamais quelle part des écartements tient à une date incompatible
 * plutôt qu'à un simple homonyme.
 */
export enum MatchDiscardReason {
  HOMONYME = "HOMONYME",
  DATE_INCOMPATIBLE = "DATE_INCOMPATIBLE",
  NATIONALITE_INCOMPATIBLE = "NATIONALITE_INCOMPATIBLE",
  PROFIL_INCOMPATIBLE = "PROFIL_INCOMPATIBLE",
  /** Même individu, mais une autre correspondance a déjà été retenue pour lui. */
  DOUBLON_PROFIL = "DOUBLON_PROFIL",
  /** L'agent revient sur son propre geste : correspondance retenue par erreur. */
  ERREUR_SAISIE = "ERREUR_SAISIE",
  AUTRE = "AUTRE",
}

/** Issue du dossier, recalculée après chaque jugement. */
export enum ScreeningOutcome {
  /** Au moins une correspondance retenue : le dossier part en revue. */
  MATCH = "MATCH",
  /** Il reste des correspondances à trancher : le dossier est retenu. */
  TO_QUALIFY = "TO_QUALIFY",
  /** Tout a été écarté : le dossier est propre, la création reprend. */
  NO_MATCH = "NO_MATCH",
  FAILED = "FAILED",
  NOT_SCREENED = "NOT_SCREENED",
}

export interface QualifiableMatch {
  matchId: string;
  /** `null` tant que la correspondance n'a pas été tranchée. */
  qualification: MatchQualification | null;
  qualifiedBy: string | null;
  qualifiedAt: string | null;
  motif: string | null;

  entityFullName: string | null;
  entityDateOfBirth: string | null;
  /** Lieu(x) de naissance rattache(s) a l'entite : avec la date, la seule donnee d'identite. */
  entityPlaceOfBirth: string | null;
  /**
   * Pays de la premiere source ayant signale l'entite. **Pas une nationalite** : un dirigeant
   * signale via « Rwanda Finance Ltd Leadership » porte RW tout en etant ivoirien. Contexte
   * uniquement, jamais confronte.
   */
  entitySourceCountry: string | null;
  entityCategory: string | null;
  /** L'entité est signalée politiquement exposée. */
  politicallyExposed: boolean | null;
  /**
   * Force du rapprochement sur le nom (0–100). Ne suffit pas à décider : un score élevé peut
   * désigner un homonyme parfait, un score faible une vraie personne exposée.
   */
  matchScore: number | null;
  /** Ce qui emporte souvent la décision, bien plus qu'un pourcentage. */
  sources: string[];
  amlTypes: string[];

  dateOfBirthStatus: DiscriminantStatus;
  placeOfBirthStatus: DiscriminantStatus;
}

export interface MatchReview {
  businessKey: string;
  customerFullName: string | null;
  customerDateOfBirth: string | null;
  customerNationality: string | null;
  customerPlaceOfBirth: string | null;
  matches: QualifiableMatch[];
  outcome: ScreeningOutcome;
  /** Quand ce compte tombe à zéro sans correspondance retenue, le dossier se débloque. */
  remainingToQualify: number;
}

export interface QualifyMatchPayload {
  confirmed: boolean;
  /** Obligatoire pour écarter. */
  motifCode?: MatchDiscardReason;
  /** Obligatoire pour écarter : sans motif, la décision n'est pas opposable. */
  motif?: string;
}
